// @flow
import layoutStyles from '../layout.module.css';
import tableStyles from './table.module.css';
import { withStyles } from '@material-ui/core/styles';
import {
  AutoSizer,
  ScrollSync,
} from 'react-virtualized';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { scrollbarSize } from 'dom-helpers';
import classNames from 'classnames';
import React from 'react';
import {
    Tooltip,
    ClickAwayListener,
} from '@material-ui/core';
import InfoOutlined from '@material-ui/icons/InfoOutlined';

import InnerContent from './InnerContent';
import CustomCheckBox from '../components/CheckBox';

import getSafe from './hooks/helpers';
import useLot from './hooks/useLot';

import Column, { columnIds } from './utils/Column';
import {
  awardColumns,
  awardValueColumn,
  AWARD_COLUMN_IDS
} from './utils/award';

import 'react-virtualized/styles.css';
import './index.css';
import updateLotAward from './services/updateLotAward';
import changeBaselineCell from './services/changeBaselineCell';
import saveBaseline from './services/saveBaseline';
import restoreBaselineValues from './services/restoreBaselineValues';
import formatBaseline from './services/formatBaseline';
import showToastMessage from './services/showToastMessage';
import showConfirmationDialog from './services/showConfirmationDialog';
import pasteBaseline from './services/pasteBaseline';
import getNumberFromString from './hooks/helpers/getNumber';
import { assignBestBidAtLot } from './hooks/helpers/getFormattedSupplierResponses';
import getBestBidColumn from './hooks/helpers/getBestBidColumn';
import { SurrogateBid } from './components/supplier_icons/surrogate';
import { checkIfAwardIsBlocked } from './utils/supplier';
import { SupplierStatusBlocked } from './components/supplier_icons/blocked';
import { Thumb } from './components/supplier_icons/thumb';
import { Flag } from './components/supplier_icons/flag';
import { parseHTML } from '../utils/excelService';
import { 
  checkSortOrderIsAscending,
  prepareLotLevelTopBids,
  prepareLotLevelFieldsBestBids,
  prepareLotLevelFieldsTopBids,
  prepareLSTLTPBestBids,
  updateLotLevelPercentageDifferenceFromBestBid,
  updateLotLevelFieldsPercentageDifferenceFromBestBid,
  updateLSTLTPPercentageDifferenceFromBestBid
} from './hooks/helpers/getTopBids';

const state = {
  overscanColumnCount: 10,
  overscanRowCount: 10,
  rowHeight: 27,
  rowCount: 100,
};

const FormulaTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgb(0, 0, 0)',
    boxShadow: theme.shadows[1],
    fontSize: 16,
    fontWeight: 500,
    fontFamily: 'Selawik',
    paddingLeft: 10,
    paddingRight: 10
  },
}))(Tooltip);

const Formula = ({ formula }) => {
  const [open, setOpen] = useState(false);
  const handleTooltipClose = () => setOpen(false)
  const handleTooltipOpen = () => setOpen(true)
  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <FormulaTooltip
        PopperProps={{
          disablePortal: true,
        }}
        onClose={handleTooltipClose}
        open={open}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={formula}
        className={tableStyles.formulaTooltip}
      >
        <span
          className={classNames(
            tableStyles.suffix,
            tableStyles.link
          )}
          onClick={handleTooltipOpen}
        >
          (f)
        </span>
      </FormulaTooltip>
    </ClickAwayListener>
  )
}

const getBaselineInputValue = (payloadId, fallback) => {
  const input = document.getElementById(`baseline-${payloadId}`);
  if (input && String(input.value).trim() !== '') {
    return input.value;
  }
  return fallback;
};

const EditBaseline = ({
  isEditable,
  setIsEditable,
  lineItems,
  lotLevelFields,
  hasLotFields,
  transformedBidColumns,
  showEditBaselineConfirmationDialog,
  responsesCountByRow,
  responseNeededForAllLineItems,
  showLineItemsWithNoResponse,
  enableLotTransformation,
}) => {
  const hasConfiguredLotTerms = typeof hasLotFields === 'boolean'
    ? hasLotFields
    : (lotLevelFields?.length ?? 0) > 0;
  const handleEditClick = () => {
    if (showEditBaselineConfirmationDialog) {
      showConfirmationDialog();
    }
    setIsEditable(true);
  };

  const handleSaveClick = (e) => {
    for (let i=0; i < lineItems.length; i++) {
      let lineItem = lineItems[i];
      const count = responsesCountByRow?.get?.(lineItem.urid);
      const isEmpty = (count === undefined || count === 0) && !responseNeededForAllLineItems;
      if (isEmpty && !showLineItemsWithNoResponse) {
        continue;
      }
      const baselineValue = getBaselineInputValue(
        lineItem.urid,
        lineItem.baseline_price
      );
      if (baselineValue === '' || baselineValue === null || baselineValue === undefined) {
        const payload = {
          message: "Some mandatory cells are empty"
        }
        showToastMessage(payload);
        return;
      }
    };
    // lot_level_fields can be non-empty in the payload even when the lot has no terms (has_lot_fields: false).
    if (hasConfiguredLotTerms && Array.isArray(lotLevelFields)) {
      for (let i=0; i < lotLevelFields.length; i++) {
        let lotField = lotLevelFields[i];
        if (lotField?.input_field_type === 'Text') {
          continue;
        }
        const lotFieldId = `lot-field-${lotField?.ucid || lotField?.lot_field_id || i}`;
        const baselineValue = getBaselineInputValue(
          lotFieldId,
          (
            lotField && lotField.baseline_value !== undefined && lotField.baseline_value !== null
              ? lotField.baseline_value
              : lotField && lotField.baseline_price !== undefined && lotField.baseline_price !== null
                ? lotField.baseline_price
                : lotField && lotField.value
          )
        );
        if (baselineValue === '' || baselineValue === null || baselineValue === undefined) {
          const payload = {
            message: "Some mandatory cells are empty"
          }
          showToastMessage(payload);
          return;
        }
      }
    }
    if (enableLotTransformation && Array.isArray(transformedBidColumns)) {
      for (let i=0; i < transformedBidColumns.length; i++) {
        let transformationField = transformedBidColumns[i];
        if (transformationField && (transformationField.ucid === 'ptf_multiplier' || transformationField.ucid === 'ptf_adder')) {
          const baselineValue = getBaselineInputValue(
            transformationField.ucid,
            (
              transformationField.baseline_value !== undefined && transformationField.baseline_value !== null
                ? transformationField.baseline_value
                : transformationField.baseline_price !== undefined && transformationField.baseline_price !== null
                  ? transformationField.baseline_price
                  : transformationField.value ?? transformationField.default_value
            )
          );
          if (baselineValue === '' || baselineValue === null || baselineValue === undefined) {
            const payload = {
              message: "Some mandatory cells are empty"
            }
            showToastMessage(payload);
            return;
          }
        }
      }
    }
    saveBaseline();
    setIsEditable(false);
  };

  const handleCancelClick = (e) => {
    setIsEditable(false);
    restoreBaselineValues();
  };

  return (
    <Fragment>
      {!isEditable && (
        <div className={tableStyles.link} onClick={handleEditClick} data-test="header-edit">
          Edit
        </div>
        )
      }
      {isEditable && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-evenly'
        }}>
          <span className={tableStyles.link} onClick={handleSaveClick} data-test="header-save">
              <img className={tableStyles.editIcons} title='Save Baseline Price changes' src='assets/icons/monitor-icons/approve.svg' />
          </span>
          <span className={tableStyles.link} onClick={handleCancelClick}>
              <img className={tableStyles.editIcons} title='Cancel Baseline Price changes' src='assets/icons/monitor-icons/disapprove.svg' />
          </span>
        </div>
        )
      }
    </Fragment>
  );
};

const BaselineCell = (props) => {
  const {
    isEditing,
    baselineColumnStyle,
    rowIndex,
    ucid,
    handleBaselinePayload,
    lineItem,
    handleFormatBaseline,
    columnIndex,
    handleBaselinePaste,
    renderValues
  } = props
  return (
    <Fragment>
      {
        isEditing
        && (
          <input
            style={baselineColumnStyle}
            className={tableStyles.awardInput}
            type="text"
            id={`baseline-${lineItem.urid}`}
            defaultValue={getSafe(() => lineItem[ucid])}
            onChange={(e) => handleBaselinePayload(e, lineItem.urid, rowIndex, ucid)}
            onBlur={(e) => handleFormatBaseline(e, rowIndex, columnIndex, lineItem.urid, ucid)}
            onPaste={(e) => handleBaselinePaste(e, rowIndex, columnIndex, ucid)}
          >
          </input>
      )}
      {
        !isEditing
        && (
          <span
            className={tableStyles.cellContent}
          >
            {
              !renderValues
              ? '-' : getSafe(() => lineItem[ucid])
            }
        </span>
      )}
    </Fragment>
  );
};

const DEFAULT_AWARD_BY = 'percentage';
const numberOfTopBidsToHighlight = 3;
let bestBidOfLot = "-"
if (!window.htmlToFormattedText) {
  window.htmlToFormattedText = function (html) {
      let tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      return tempDiv.innerText || tempDiv.textContent;
  };
}
function App (props: any): React$Element<any>|null {

  const {
    award,
    baselineTotal,
    currencySymbol,
    eventId,
    eventInfo,
    eventSavingsTotal,
    hiddenColumns,
    hideSuppliers,
    lotId,
    localeCode,
    optimizedAt,
    filterValue,
    isLoading,
    baseUrl,
    subDomain,
    scenarioId,
    accessToken,
    lotPricingSheetResponse,
    participatingSuppliersMap,
    isAwardBlockedAtEventLevel,
    isAwardBlockedAtProfileLevel,
    rankWiseStyles,
    lotLevelFields,
    viewAwardScenario
  } = props;
  const indexBasedRankWiseStyles = (() => {
    const generatedClasses = {};
    if (rankWiseStyles) {
      const bgColors = rankWiseStyles.bg_colors
      const fontColors = rankWiseStyles.font_colors
      Object.entries(bgColors).forEach(([rank, bgColor], index) => {
        const fontColor = fontColors[rank]
        const className = `rank${index}`;
        const style = document.createElement('style');
        style.type = 'text/css';
        style.innerHTML = `
          .ra-table .${className} {
            background-color: ${bgColor};
          }
          .ra-table .${className} span {
            color: ${fontColor};
          }
        `;
        document.getElementsByTagName('head')[0].appendChild(style);
        generatedClasses[index] = className;
      });
    }
    return generatedClasses;
  })();
  const blockedConfig = {
    isAwardBlockedAtEventLevel,
    isAwardBlockedAtProfileLevel
  };

  const isOptimizedAtLot = optimizedAt === 'lot';
  const sortBidsAscending = checkSortOrderIsAscending(eventInfo);

  const awardRef = useRef();
  const baseUrlRef = useRef();
  const dataRef = useRef();
  const eventRef = useRef();
  const localeRef = useRef();
  const lotRef = useRef();
  const sharesRef = useRef();
  const optimizedRef = useRef();
  const subDomainRef = useRef();
  const scenarioIdRef = useRef();

  const formatValue = (value, decimalSpaces) => {
    const formatter = new Intl.NumberFormat(localeRef.current, {
      maximumFractionDigits: decimalSpaces,
      minimumFractionDigits: decimalSpaces
    });
    return formatter.format(value);
  };
  const [data, setData] = useLot({
    eventId,
    eventInfo,
    numberOfTopBidsToHighlight,
    lotId,
    localeRef,
    subDomain,
    baseUrl,
    scenarioId,
    accessToken,
    lotPricingSheetResponse,
    lotLevelFields
  });

  if (
    data.bestBids
    && data.bestBids.get('lotLevelTopBids')
    && data.bestBids.get('lotLevelTopBids')[0]
  ){
    bestBidOfLot = data.bestBids.get('lotLevelTopBids')[0]
  }else {
    bestBidOfLot = '-'
  }

  const EXCLUDED_FIELD = 'checkbox_value';
  const IGNORED_FIELD = 'baseline_checkbox_value';

  const defaultShares = {
    lotId: null,
    lot_level_shares: {},
    line_item_level_shares: {},
    [EXCLUDED_FIELD]: [],
    [IGNORED_FIELD]: []
  };

  const [shares, setShares] = useState({
    ...defaultShares
  });

  const getLineShares = (optimizedAt, shares) => {
    return optimizedAt === 'line' ? shares.line_item_level_shares : {};
  }

  const getLotShares = (optimizedAt, shares) => {
    return optimizedAt === 'lot' ? shares.lot_level_shares : {};
  }

  useEffect(() => {
    awardRef.current = award;
    baseUrlRef.current = baseUrl;
    dataRef.current = data;
    eventRef.current = eventId;
    lotRef.current = lotId;
    optimizedRef.current = optimizedAt;
    sharesRef.current = shares;
    subDomainRef.current = subDomain;
    scenarioIdRef.current = scenarioId;
  }, [
    award,
    baseUrl,
    data,
    eventId,
    lotId,
    optimizedAt,
    shares,
    subDomain,
    scenarioId
  ]);

  const [errors, setErrors] = useState({
    lot: false,
    line: []
  });

  useEffect(() => {
    if (!award) {
      return;
    }
    const oldLotId = shares.lotId;
    const newAward: any = awardRef.current;
    const newLotId = newAward.lot_id;
    if (newLotId === oldLotId) {
      return;
    }
    if (newAward.errors) {
      setErrors(newAward.errors);
    }
    setShares({
      lotId: newLotId,
      lot_level_shares: newAward.lot_level_shares,
      line_item_level_shares: newAward.line_item_level_shares,
      [EXCLUDED_FIELD]: newAward[EXCLUDED_FIELD],
      [IGNORED_FIELD]: newAward[IGNORED_FIELD]
    });
  }, [award]);

  useEffect(() => {
    // ===========================
    // award related API call is
    // happening, reset everything
    // ===========================
    if (isLoading) {
      // ============
      // reset errors
      // ============
      setErrors({
        lot: false,
        line: []
      });
      // ============
      // reset shares
      // ============
      setShares({
        ...defaultShares,
        lotId
      });
    }
  }, [isLoading]);

  const lineErrorSet: Set<string> = useMemo(() => {
    return new Set((errors.line: any));
  }, [errors.line]);

  async function onUpdate (e) {
    // =========================
    // award particular lot
    // or line item to suppliers
    // =========================
    const currentAwardBy = getSafe(() => (awardRef.current : any).award_by);
    const currentShares = sharesRef.current || {};
    const optimizedAt = optimizedRef.current;
    const lineItemShares = getLineShares(optimizedAt, currentShares);
    const lotShares = getLotShares(optimizedAt, currentShares);
    const optimizedAtLevel = optimizedRef.current === 'lot' ? 'lot_level' : 'line_item_level';
    let ignoredValues = currentShares[IGNORED_FIELD];
    let excludedValues = currentShares[EXCLUDED_FIELD];
    const currentData = dataRef ? dataRef.current : null;
    if (currentData !== null && currentData !== undefined) {
      const excludedSet = new Set(excludedValues);
      const ignoredSet = new Set(ignoredValues);
      const { lineItems } = currentData;

      lineItems.forEach(row => {
        const count = currentData.responsesCountByRow.get(row.urid);
        if (
          (count === undefined || count === 0) && !currentData.response_needed_for_all_line_items
        ) {
          excludedSet.add(row.urid);
          ignoredSet.add(row.urid);
        }
      })
      excludedValues = Array.from(excludedSet);
      ignoredValues = Array.from(ignoredSet);
    }
    const payload = {
      award_by: currentAwardBy || DEFAULT_AWARD_BY,
      [IGNORED_FIELD]: ignoredValues,
      [EXCLUDED_FIELD]: excludedValues,
      eventId: eventRef.current,
      line_item_level_shares: lineItemShares,
      lot_id: lotRef.current,
      lot_level_shares: lotShares,
      optimized_at: optimizedAtLevel,
    };
    const serverOptions = {
      subDomain: subDomainRef.current,
      baseUrl: baseUrlRef.current,
      accessToken
    };
    const result = await updateLotAward(payload, serverOptions, scenarioIdRef.current);
    if (result && result.errors) {
      const currentLotErrors = result.errors.filter(e => e.lot_id === lotRef.current);
      if (optimizedRef.current === 'lot') {
        setErrors({
          line: [],
          lot: true
        });
      }
      else {
        setErrors({
          line: currentLotErrors.map(e => e.urid),
          lot: false
        });
      }
    }
    else {
      setErrors({
        line: [],
        lot: false
      });
    }
    if (
      result
      && result.data
      && result.data.supplier_responses
    ) {
      const {
        data: {
          supplier_responses: newResponses
        }
      } = result;

      const formatValue = (value, decimalSpaces) => {
        const formatter = new Intl.NumberFormat(localeRef.current, {
          maximumFractionDigits: decimalSpaces,
          minimumFractionDigits: decimalSpaces
        });
        return formatter.format(value);
      };
      
      setData(data => {
        const responses = {
          ...data.responses
        };
        const {
          normalSupplierColumns,
          alternateSupplierColumns
        } = data;
        const decimalMap = new Map(
          [
            ...normalSupplierColumns.map(c => [c.ucid, c.decimal_spaces]),
            ...alternateSupplierColumns.map(c => [c.ucid, c.decimal_spaces])
          ]
        );
        const bestBidColumnId = getBestBidColumn(data);
        Object.keys(responses).forEach(supplierId => {
          const response = responses[supplierId];
          const serverSentResponse = newResponses[supplierId];
          if (!supplierId || !(serverSentResponse && serverSentResponse.totals_row)) {
            return;
          }
          const { totals_row } = serverSentResponse;
          if (!(totals_row && typeof totals_row === 'object')) {
            return;
          }
          Object.keys(totals_row).forEach(columnId => {
            const rawValue = totals_row[columnId];
            if (!(
              typeof rawValue === 'number'
              || typeof rawValue === 'string'
            )) {
              return;
            }
            if (!decimalMap.has(columnId)) {
              return;
            }
            const convertedValue = getNumberFromString(rawValue);
            if (convertedValue <= 0) {
              return;
            }
            totals_row[columnId] = formatValue(convertedValue, decimalMap.get(columnId));
          })
          response.totals_row = {
            ...response.totals_row,
            ...totals_row
          };
          
          // Also update lot level field responses and totals
          if (serverSentResponse.lot_field_responses) {
            response.lot_field_responses = serverSentResponse.lot_field_responses;
          }
          if (serverSentResponse.lot_sub_total !== undefined) {
            response.lot_sub_total = serverSentResponse.lot_sub_total;
          }
          if (serverSentResponse.lot_total_price !== undefined) {
            response.lot_total_price = serverSentResponse.lot_total_price;
          }
          if (!decimalMap.has(bestBidColumnId)) {
            return;
          }
          const decimalSpaces = decimalMap.get(bestBidColumnId);
          const bestBid = getNumberFromString(
            formatValue(result.data.lot_event_saving.total, decimalSpaces)
          );
        });
        const lotLevelTopBids = prepareLotLevelTopBids(
          data.responses,
          eventInfo,
          numberOfTopBidsToHighlight,
          formatValue,
          bestBidColumnId,
          data.column_info
        )
        data.bestBids.set('lotLevelTopBids', lotLevelTopBids)
        
        // Calculate best bids for lot level fields
        const lotLevelFieldsBestBids = prepareLotLevelFieldsBestBids(
          data.responses,
          lotLevelFields,
          eventInfo,
          formatValue,
          bestBidColumnId,
          data.column_info
        )
        data.bestBids.set('lotLevelFieldsBestBids', lotLevelFieldsBestBids)
        
        // Calculate top bids for lot level fields (for ranking)
        const lotLevelFieldsTopBids = prepareLotLevelFieldsTopBids(
          data.responses,
          lotLevelFields,
          eventInfo,
          numberOfTopBidsToHighlight,
          formatValue,
          bestBidColumnId,
          data.column_info
        )
        data.bestBids.set('lotLevelFieldsTopBids', lotLevelFieldsTopBids)
        
        // Calculate best bids for LST and LTP
        const lstLtpBestBids = prepareLSTLTPBestBids(
          data.responses,
          eventInfo,
          formatValue,
          bestBidColumnId,
          data.column_info
        )
        data.bestBids.set('lstLtpBestBids', lstLtpBestBids)
        
        // Calculate percentage differences for lot level fields
        updateLotLevelFieldsPercentageDifferenceFromBestBid(
          data.responses,
          lotLevelFields,
          lotLevelFieldsBestBids,
          formatValue
        )
        
        // Calculate percentage differences for LST and LTP
        updateLSTLTPPercentageDifferenceFromBestBid(
          data.responses,
          lstLtpBestBids,
          formatValue,
          bestBidColumnId
        )
        
        if (
          lotLevelTopBids
          && lotLevelTopBids[0]
        ){
          
          updateLotLevelPercentageDifferenceFromBestBid(
            responses,
            lotLevelTopBids[0],
            bestBidColumnId,
            formatValue
          )
        }
        return {
          ...data,
          responses
        }
      });
    }
  }

  const isAwardDraft = getSafe(() => award.isDraft);
  const lineItemShares = getLineShares(optimizedAt, shares);
 
  const AWARD_BY = {
    'quantity': 'Qty',
    'percentage': '%'
  };

  const awardBy = getSafe(() => AWARD_BY[award.award_by || DEFAULT_AWARD_BY]);

  const [headerHeight, setHeaderHeight] = useState({
    supplierNameHeight: 43,
    termNameHeight: 27,
    totalsHeight: 27
  });

  const [hideHorizontalScroll, setHideHorizontalScroll] = useState(false);

  const contentGrid = useRef();
  const dummyContentGrid = useRef();

  const setContentRef = ref => contentGrid.current = ref;
  const setDummyContentRef = ref => dummyContentGrid.current = ref;

  const { buyerColumns: columnsFilledByBuyer } = data;
  const buyerColumns = columnsFilledByBuyer;
  const hiddenColumnSet = new Set(hiddenColumns);

  useEffect(() => {
    localeRef.current = localeCode;
  }, [localeCode]);

  const {
    suppliersOrder,
    suppliers,
    normalSupplierColumns,
    alternateSupplierColumns,
    lineItems,
    responses,
    totals_row,
    buyer_totals,
    surrogate_bidders_data,
    isTransformationAppliedAtLineItemLevel,
    bidAction,
    responsesCountByRow,
    responseNeededForAllLineItems,
    bestBids,
    bestBidColumnId
  } = data;

  const sizeOfScroll = scrollbarSize();

  const getHeaderHeight = useCallback(() => {
    // Totals row has been moved out of the header,
    // so header height is just supplier + term name.
    return (
      headerHeight.supplierNameHeight
      + headerHeight.termNameHeight
    );
  }, [headerHeight])

  const lotSummaryAdditionalRows = useMemo(() => {
    const isBidAction3 = bidAction === 'b' || bidAction === '3';
    const isLotTransformationEnabled = !!lotPricingSheetResponse?.data?.enable_lot_transformation;
    const hasConfiguredLotTerms = typeof lotPricingSheetResponse?.data?.has_lot_fields === 'boolean'
      ? !!lotPricingSheetResponse.data.has_lot_fields
      : (lotLevelFields?.length ?? 0) > 0;
    if (isBidAction3) {
      return 1 + (isLotTransformationEnabled ? 4 : 0);
    }
    if (hasConfiguredLotTerms) {
      const n = lotLevelFields?.length ?? 0;
      return (n + 5) + 1 + (isLotTransformationEnabled ? 1 : 0) + (isLotTransformationEnabled ? 4 : 0);
    }
    // No extra blank row after Item Sub Total unless lot terms exist (see InnerContent lotLevelRowIndex === 1).
    return 1 + (isLotTransformationEnabled ? 1 : 0) + (isLotTransformationEnabled ? 4 : 0);
  }, [bidAction, lotPricingSheetResponse, lotLevelFields]);

  const totalTableHeight = useMemo(() => {
    const { lineItems } = data;
    const showLineItemsWithNoResponse = filterValue === 'lineItemWithNoRes';
    const visibleLineItems = lineItems.filter(item => 
      !showLineItemsWithNoResponse 
      || (
        (data.responsesCountByRow.get(item.urid) === undefined || data.responsesCountByRow.get(item.urid) === 0)
        && !data.response_needed_for_all_line_items
      )
    )
    const additionalRows = lotSummaryAdditionalRows;
    const totalRows = visibleLineItems.length + additionalRows;
    const visibleRowsLength = Math.min(totalRows, 12 + additionalRows);
    return (
      getHeaderHeight()
      + state.rowHeight * visibleRowsLength
      + (hideHorizontalScroll ? 0 : sizeOfScroll)
    )
  }, [
    data,
    filterValue,
    sizeOfScroll,
    hideHorizontalScroll,
    getHeaderHeight,
    lotSummaryAdditionalRows
  ]);

  const showLineItemsWithNoResponse = filterValue === 'lineItemWithNoRes';

  const getSupplierColumn = (index) => {
    const supplierIndex = index - buyerColumns.length;
    const supplierId = suppliersOrder[supplierIndex];
    const supplierColumn = new Column(suppliers[supplierId])
    return supplierColumn
  }

  const visibleFilter = (columns: any[]): any[] => {
    return (
      columns.filter(c => !hiddenColumnSet.has(c.ucid))
    )
  };

  const getColumnWidthHelper = ({ index }) => {
    const FIXED_COLUMN_WIDTHS = {
      [AWARD_COLUMN_IDS.excluded]: 100,
      [AWARD_COLUMN_IDS.ignored]: 70,
      sr_no: 70,
      item_name: 200,
      lot_name: 180,
      quantity: 104,
      baseline_price: 160,
      event_saving: 160,
      best_bid: 170,
      award_savings: 160,
      uom: 104
    };
    const buyerColumn = getColumnInstance(0, index);
    if (hiddenColumns && hiddenColumns.includes(buyerColumn.ucid)) {
      return 0;
    }
    if (buyerColumn.ucid === 'quantity' && bidAction === 'e') {
      return 130;
    }
    if (buyerColumn.ucid && buyerColumn.ucid in FIXED_COLUMN_WIDTHS) {
      return FIXED_COLUMN_WIDTHS[buyerColumn.ucid];
    }
    const supplierColumn = buyerColumn;
    const supplier_id = supplierColumn.safeAccess('supplier_id');
    if (supplier_id in hideSuppliers) {
      return 0;
    }
    if (
      supplierColumn.isValid
      && !supplierColumn.isBuyerFilled
    ) {
      // ===========================
      // supplier section to render
      // calculate all subcolumns
      // and interpolate width based 
      // on total number of columns
      // ===========================
      const additionalWidth = isAwardDraft ? 135 : 0;
      const columns = (
        supplierColumn.isAlternateBid
        ? alternateSupplierColumns
        : normalSupplierColumns
      );
      if (columns.length === 1 && !isAwardDraft) {
        return 180;
      }
      return visibleFilter(columns).length * 135 + additionalWidth;
    }
    return 150;
  }

  function recomputeGrid() {
    const recompute = ref => ref.current ? ref.current.recomputeGridSize() : {};
    recompute(contentGrid);
    recompute(dummyContentGrid);
  }

  function setUpdatedHeights(result) {
    setHeaderHeight(heights => ({
      ...heights,
      ...result
    }));
  }

  // =======================================
  // To adjust the header height in order to
  // accomodate changes in different column
  // content height
  // 
  // E.g.
  // In award scenario, there is a
  // column Ignore Spend which requires
  // more height to render all content
  // =======================================
  const measureHeader = () => {
    const cellContainers = document.querySelectorAll(`.dummyParent`);
    if (cellContainers.length) {
      const result = {
        supplierNameHeight: 43,
        termNameHeight: 27
      };
      const getHeight = (elem: HTMLElement) => elem.getBoundingClientRect().height;
      for(let i = 0; i < cellContainers.length; i++) {
        const currentContainer = cellContainers[i];
        const supplierNameDiv = ((currentContainer.querySelector('div:nth-child(1)'): any) : HTMLElement);
        result.supplierNameHeight = Math.max(result.supplierNameHeight, getHeight(supplierNameDiv));
        const termNameDiv = ((currentContainer.querySelector('div:nth-child(2)'): any): HTMLElement);
        result.termNameHeight = Math.max(result.termNameHeight, getHeight(termNameDiv));
      }
      const isAwardDraft = award && award.isDraft;
      if (isAwardDraft && result.supplierNameHeight < 72) {
        result.supplierNameHeight = 72;
      }
      const needsToUpdate = (
        result.supplierNameHeight !== headerHeight.supplierNameHeight
        || result.termNameHeight !== headerHeight.termNameHeight
        );
      if (needsToUpdate) {
        setUpdatedHeights(result);
      }
    }
  };

  const getExcludeIgnoredArray = fieldName => new Set(shares ? shares[fieldName] : []);
  const excludedSet = useMemo(
    () => {
      const excludedSetInterim = getExcludeIgnoredArray(EXCLUDED_FIELD);
      if (Array.isArray(lineItems)) {
        lineItems.forEach(row => {
          const count = responsesCountByRow.get(row.urid);
          if (
            (count === undefined || count === 0) && !responseNeededForAllLineItems
          ) {
            excludedSetInterim.add(row.urid);
          }
        })
      }
      return excludedSetInterim;
    }
  , [shares[EXCLUDED_FIELD], lineItems]);

  const ignoredSet = useMemo(
    () => {
      const ignoredSetInterim = getExcludeIgnoredArray(IGNORED_FIELD);
      if (Array.isArray(lineItems)) {
        lineItems.forEach(row => {
          const count = responsesCountByRow.get(row.urid);
          if (
            (count === undefined || count === 0) && !responseNeededForAllLineItems
          ) {
            ignoredSetInterim.add(row.urid);
          }
        })
      }
      return ignoredSetInterim;
    }
  , [shares[IGNORED_FIELD], lineItems]);

  useEffect(() => {
    setTimeout(() => {
      // =========================
      // wait 1 second for DOM to
      // reflect the new height in
      // dummy container
      // =========================
      measureHeader();
    }, 1000);
  }, [
    hiddenColumns,
    hideSuppliers,
    suppliers,
    award
  ]);

  useEffect(() => {
    recomputeGrid();
  }, [
    hiddenColumns,
    hideSuppliers,
    filterValue,
    headerHeight,
    data,
    award
  ]);

  const hasConfiguredLotTerms = typeof lotPricingSheetResponse?.data?.has_lot_fields === 'boolean'
    ? !!lotPricingSheetResponse.data.has_lot_fields
    : (lotLevelFields?.length ?? 0) > 0;

  const supplierHasPostAuctionLineItemResponses = (supplierId) => {
    const supplierResponse = responses[supplierId];
    if (!supplierResponse?.row_data) {
      return false;
    }
    if (Array.isArray(supplierResponse.row_data)) {
      return supplierResponse.row_data.length > 0;
    }
    return Object.keys(supplierResponse.row_data).length > 0;
  };

  const shouldHidePostAuctionSupplierTotals = (supplierId) => (
    (bidAction === 'c' || bidAction === '2')
    && hasConfiguredLotTerms
    && !supplierHasPostAuctionLineItemResponses(supplierId)
  );

  const hasAnyPostAuctionLineItemResponses = suppliersOrder.some(
    (supplierId) => supplierHasPostAuctionLineItemResponses(supplierId)
  );

  const shouldHidePostAuctionLineItemBuyerTotals = (
    (bidAction === 'c' || bidAction === '2')
    && hasConfiguredLotTerms
    && !hasAnyPostAuctionLineItemResponses
  );

  const getSupplierTotals = (supplierId, columnData) => {
    if (filterValue === 'lineItemWithNoRes') {
      return ''
    }
    if (shouldHidePostAuctionSupplierTotals(supplierId)) {
      return '';
    }
    const column = new Column(columnData);
    const { transformation_costs_data } = data;
    if (
      column.isPTF
      && !data.isTransformationAppliedAtLineItemLevel
    ) {
        const supplier_id = supplierId.toString().split('_')[0];
        return getSafe(() => transformation_costs_data[supplier_id][columnData.ucid]);
    }
    if (column.isPTF && data.isTransformationAppliedAtLineItemLevel) {
      return '';
    }
    return getSafe(() => responses[supplierId]['totals_row'][columnData.ucid]) || '';
  }

  const getLotShare = (supplierId) => {
    if (isLotExcluded) {
      return '';
    }
    if (optimizedAt === 'line') {
      return '-';
    }
    return getSafe(() => shares.lot_level_shares[supplierId]) || '';
  }

  const changeLotShare = e => {
    const supplierId = e.target.getAttribute('data-supplier');
    const value = e.target.value;
    if (isNaN(value)) {
      return;
    }
    let parsedValue = parseInt(value, 10);
    if (awardBy === '%') {
      parsedValue = Math.min(100, parsedValue);
    }
    const lot_level_shares = { ...shares.lot_level_shares };
    if (isNaN(parsedValue)) {
      delete lot_level_shares[supplierId];
    }
    else {
      lot_level_shares[supplierId] = parsedValue;
    }
    setShares(shares => {
      return ({
        ...shares,
        lot_level_shares
      })
    });
  };

  const getRankWiseClassForLot = (supplierId, column) => {
    if (
      filterValue === 'lineItemWithNoRes'
      || isLotExcluded
      || column.ucid !== bestBidColumnId
      || new Column(column).isPTF
    ) {
      return null;
    }
    if (shouldHidePostAuctionSupplierTotals(supplierId)) {
      return null;
    }
    if (
      responses
      && responses[supplierId]
      && responses[supplierId].totals_row
      && responses[supplierId].totals_row[bestBidColumnId]
    ) {
      const bidValue = responses[supplierId].totals_row[bestBidColumnId];
      const topBidsOfLot = bestBids.get('lotLevelTopBids');
      const rankOfBidValue = topBidsOfLot.indexOf(bidValue);
      if (rankOfBidValue === -1) {
        return null;
      } else{
        return indexBasedRankWiseStyles[rankOfBidValue]
      }
    }
    return null
  }

  const isLotExcluded = Array.isArray(lineItems) && excludedSet.size === lineItems.length;
  const isLotIgnored = Array.isArray(lineItems) && excludedSet.size > 0 && ignoredSet.size === excludedSet.size;
  const isLotFullyGreyedOut = isLotExcluded && Array.isArray(lineItems) && lineItems.length > 0
    && lineItems.every((item) => ignoredSet.has(`${item.urid}`));

  const getAwardSavingAtLine = (urid) => {
    if (
      award
      && award.line_item_award_saving
      && award.line_item_award_saving[urid] !== null
      && award.line_item_award_saving[urid] !== undefined
      && award.line_item_award_saving[urid] !== ''
    ) {
      return award.line_item_award_saving[urid];
    }
    return 0;
  }

  const awardSavingTotal = useMemo(() => {
    const { lineItems } = data;
    if (isOptimizedAtLot) {
      return (
        (award && award.lot_award_saving !== null && award.lot_award_saving !== undefined && award.lot_award_saving !== '')
        ? formatValue(award.lot_award_saving, 2)
        : ''
      );
    }
    else {
      if (Array.isArray(lineItems)) {
        const totalAwardSaving = lineItems.reduce((acc, lineItem) => acc + getAwardSavingAtLine(lineItem.urid), 0);
        return formatValue(totalAwardSaving, 2);
      }
      return '';
    }
  }, [award, data, isOptimizedAtLot]);

  const awardColumnStyle = useMemo(() => {
    const awardStyle = {
      width: 117 - (awardBy === '%' ? 11 : 0),
      textAlign: 'right',
      paddingRight: 5
    };
    return awardStyle;
  }, [awardBy]);

  const baselineColumnStyle = {
    width: '100%',
    textAlign: 'right',
    fontSize: 13,
    fontFamily: 'Selawik'
  }

  const [isColumnEditable, _setIsColumnEditable] = useState(false);
  const setIsColumnEditable = (value) => {
    _setIsColumnEditable(value);
  }

  useEffect(() => {
    setIsColumnEditable(false);
  }, [lotId]);

  const makeHeaderRenderer = (isDummy=false) => {
    const _headerRenderer = ({
      columnIndex,
      key,
      style
    }) => {
      const {
        buyerColumns: columns,
      } = data;
      const buyerColumn = getColumnInstance(0, columnIndex);
      const supplierColumn = buyerColumn;
      const supplierId = supplierColumn.safeAccess('supplier_id');
      const surrogateSupplierId = supplierColumn.isAlternateBid && typeof supplierId === 'string'
        ? supplierId.replace('_alternate', '')
        : supplierId;
      const surrogateBid = surrogate_bidders_data[supplierId] || surrogate_bidders_data[surrogateSupplierId];
      const fixHeight = rowName => !isDummy ? {
        style: {
          height: headerHeight[rowName]
        }
      } : {}
      const supplierNameStyles = fixHeight('supplierNameHeight');
      const termNameStyles = fixHeight('termNameHeight');
      const totalsStyles = fixHeight('totalsHeight');
      const subColumns = supplierColumn.columns.slice();
      if (isAwardDraft) {
        subColumns.push(
          awardValueColumn({
            awardBy,
            isAwardDraft,
            optimizedAt
          })
        );
      }

      // Using component-level getLotShare and changeLotShare functions
      const showError = isAwardDraft && optimizedAt === 'lot' && errors && errors.lot === true;
      // Use the same supplier_id as getColumnInstance / award inputs — not a separately
      // derived index — so the header always matches the column bound to lot_level_shares.
      const supplierIndexFallback = columnIndex - columns.length - (isAwardDraft ? 3 : 0);
      const supplierName = (
        (supplierId != null && suppliers[supplierId] && suppliers[supplierId].supplier_name)
        || getSafe(() => suppliers[suppliersOrder[supplierIndexFallback]].supplier_name)
        || ''
      );
      let trimmedSupplierName = supplierName;
      if (supplierName && supplierName.length > 17) {
        trimmedSupplierName = supplierName.slice(0, 17) + '...';
      }
      const isDutchPartial = bidAction === 'e'
      const showEditBaselineButton = getSafe(() => lotPricingSheetResponse.data.show_edit_baseline_button);
      const showEditBaselineConfirmationDialog = getSafe(() => lotPricingSheetResponse.data.show_edit_baseline_confirmation_dialog);
      const transformedBidColumns = getSafe(() => lotPricingSheetResponse.data.transformedBidColumns) || [];
      const enableLotTransformation = !!lotPricingSheetResponse?.data?.enable_lot_transformation;

      return (
        <div
          key={key}
          style={{
            ...style,
          }}
          className={classNames(tableStyles.headerCell)}
        >
          {
            buyerColumn.isBuyerFilled ? (
              <div className={classNames(
                  layoutStyles.flex,
                  layoutStyles.col
                )}>
                <div className={classNames(
                    tableStyles.row,
                    { [tableStyles.supplierName]: !isDummy },
                    tableStyles.borderTop,
                    tableStyles.borderRight
                  )}
                  style={{
                    height: headerHeight.supplierNameHeight
                  }}
                  data-test="header"
                  data-column={buyerColumn.termName}
                >
                  <div>
                    {buyerColumn.termName}
                  </div>
                  <div>
                    {
                      buyerColumn.ucid === AWARD_COLUMN_IDS.ignored && (
                        <FormulaTooltip
                          title="Checked line items will not count towards Baseline Spend and Event Savings"
                          placement="top"
                        >
                          <InfoOutlined />
                        </FormulaTooltip>
                      )
                    }
                  </div>
                </div>
                <div className={classNames(
                    tableStyles.row,
                    tableStyles.borderRight,
                  )}
                  style={{
                    height: headerHeight.termNameHeight
                  }}
                  >
                  {
                    buyerColumn.ucid === AWARD_COLUMN_IDS.excluded
                    ? (
                      <CustomCheckBox
                        checked={isLotExcluded}
                        disabled={viewAwardScenario}
                        onChange={onExcludeAllChange}
                      />
                    ) : buyerColumn.ucid === AWARD_COLUMN_IDS.ignored
                    ? (
                      <CustomCheckBox
                        checked={isLotIgnored}
                        disabled={viewAwardScenario}
                        onChange={onIgnoreAllChange}
                      />
                    ) : (buyerColumn.termName === 'Baseline Price' || buyerColumn.ucid === 'baseline_price')
                        && !isAwardDraft
                        && !isDutchPartial
                        && showEditBaselineButton && (
                      <EditBaseline
                        setIsEditable={setIsColumnEditable}
                        isEditable={isColumnEditable}
                        lineItems={lineItems}
                        lotLevelFields={lotLevelFields}
                        hasLotFields={lotPricingSheetResponse?.data?.has_lot_fields}
                        transformedBidColumns={transformedBidColumns}
                        showEditBaselineConfirmationDialog={showEditBaselineConfirmationDialog}
                        responsesCountByRow={responsesCountByRow}
                        responseNeededForAllLineItems={responseNeededForAllLineItems}
                        showLineItemsWithNoResponse={showLineItemsWithNoResponse}
                        enableLotTransformation={enableLotTransformation}
                      />
                    )
                  }
                </div>
                {/* Totals row moved to Item Sub Total row - hidden in header */}
                <div className={classNames(
                    tableStyles.row,
                    tableStyles.borderTop,
                    tableStyles.borderRight,
                    tableStyles.totalCell,
                    {
                      'error-cell': showError
                    }
                  )}
                  style={{
                    height: 0,
                    display: 'none'
                  }}
                  data-column={buyerColumn.termName}
                  data-rowid="totals"
                >
                </div>
              </div>
            ) : (
                <div className={classNames(
                  tableStyles.borderTop,
                  { 'dummyParent': isDummy }
                )}>
                  <div
                    className={classNames({
                      [tableStyles.supplierName]: !isDummy,
                      [tableStyles.pad5]: isDummy,
                      [tableStyles.borderRight]: true
                    })
                    }
                    {...supplierNameStyles}
                  >
                    <div
                      className={classNames({
                        'dummySupplierName': isDummy
                      })}
                      data-test="supplier-name"
                      title={buyerColumn.isAlternateBid ? `${supplierName} (Alternate Bid)` : supplierName}
                    >
                      {trimmedSupplierName}
                      {supplierColumn.isAlternateBid && ' (Alternate Bid)'}
                      {surrogateBid &&
                        <div>Surrogate response by {surrogateBid.updated_by_name}</div>
                      }
                    </div>
                  </div>
                  <div
                    className={classNames(
                      layoutStyles.flex,
                      layoutStyles.row,
                      { [tableStyles.row]: !isDummy },
                      tableStyles.borderBottom,
                      tableStyles.borderTop,
                      tableStyles.contentCenter,
                      tableStyles.pad0,
                      tableStyles.headerNames
                    )}
                    {...termNameStyles}
                  >
                    {
                      subColumns
                        .filter(({ ucid }) => !hiddenColumnSet.has(ucid))
                        .map((subColumn, key) => (
                        <div
                          key={key}
                          className={classNames(
                            layoutStyles.flex1,
                            layoutStyles.flex,
                            tableStyles.borderRight,
                            tableStyles.contentCenter,
                            tableStyles.padCell
                          )}>
                            <div className={classNames(
                              layoutStyles.flex
                            )}>
                              <span
                                data-column={subColumn.term_name}
                                data-supplier={supplierColumn.isAlternateBid ? `${supplierName} (Alternate Bid)` : supplierName}
                              >
                                {subColumn.term_name}
                              </span>
                              {
                                subColumn.who_fills === 'Formula' && (
                                  <Formula {...subColumn} />
                                )
                              }
                            </div>
                        </div>
                      ))
                    }
                  </div>
                  {/* Totals row moved to Item Sub Total row - hidden in header */}
                  <div
                    data-test="totals"
                    className={classNames(
                      layoutStyles.flex,
                      layoutStyles.row,
                      tableStyles.row,
                      tableStyles.borderBottom,
                      tableStyles.contentCenter,
                      tableStyles.pad0,
                      {
                        'error-cell': showError
                      }
                    )}
                    style={{
                      height: 0,
                      display: 'none'
                    }}
                  >
                  </div>
                </div>
            )
          }
        </div>
      )
    };
    return _headerRenderer;
  }

  const _headerRenderer = makeHeaderRenderer();
  const _dummyHeaderRenderer = makeHeaderRenderer(true);

  const getEventSavings = (row) => {
    if (shouldHidePostAuctionLineItemBuyerTotals) {
      return '-'
    }
    const count = responsesCountByRow.get(row.urid);
    if (filterValue !== 'allLineItems' 
      || optimizedAt === 'lot'
      || ((count === undefined || count === 0) && !responseNeededForAllLineItems)
      || !buyer_totals.event_saving_at_line[row.urid]) {
      return '-'
    }
    return buyer_totals.event_saving_at_line[row.urid].event_saving
  }

  const columnCount = (isAwardDraft ? 3 : 0) + buyerColumns.length + suppliersOrder.length;

  if (!(data && data.buyerColumns && data.buyerColumns.length > 0)) {
    return null;
  }

  const getRankWiseClassForLineItem = (rowIndex, columnIndex, ucid) => {
    // Skip ranking for lot level rows
    if (rowIndex >= lineItems.length) {
      return null;
    }
    
    const supplierColumn = getColumnInstance(rowIndex, columnIndex);

    const supplierId = supplierColumn.safeAccess('supplier_id');
    const row = getSafe(() => lineItems[rowIndex]);
    const subColDef = supplierColumn.columns && supplierColumn.columns.find(c => c.ucid === ucid);
    if (subColDef && new Column(subColDef).isPTF) {
      return null;
    }
    if (ucid !== bestBidColumnId) {
      return null;
    }
    if (
      responses
      && responses[supplierId]
      && responses[supplierId].row_data
      && responses[supplierId].row_data[row.urid]
    ) {
      const bidValue = responses[supplierId].row_data[row.urid][bestBidColumnId];
      const lineItemBestBids = bestBids.get('lineItemLevelTopBids');
      const currentLineItemBestBids = lineItemBestBids && lineItemBestBids.get(row.urid);
      if (!currentLineItemBestBids || !Array.isArray(currentLineItemBestBids)) {
        return null;
      }
      const rankOfBidValue = currentLineItemBestBids.indexOf(bidValue);
      if (rankOfBidValue === -1) {
        return null;
      } else{
        return indexBasedRankWiseStyles[rankOfBidValue]
      }
    }
    return null
  }

  const getRankWiseClassForLotLevelField = (fieldUcid, supplierId, ucid) => {
    const lotFieldMeta = Array.isArray(lotLevelFields)
      ? lotLevelFields.find(
        f => `${f.ucid}` === `${fieldUcid}` || `${f.lot_field_id}` === `${fieldUcid}`
      )
      : null;
    // Color coding only for price lot terms (not Text, Percentage, Number, etc.)
    if (!lotFieldMeta || lotFieldMeta.input_field_type !== 'Price') {
      return null;
    }
    if (lotFieldMeta.price_transformation_factor === 'yes') {
      return null;
    }
    if (ucid !== bestBidColumnId || !fieldUcid || !supplierId) {
      return null;
    }
    
    let bidValue = '';
    
    // Get the bid value for this supplier and field
    if (responses && responses[supplierId] && responses[supplierId].lot_field_responses) {
      const lotFieldResponses = responses[supplierId].lot_field_responses;
      if (lotFieldResponses && lotFieldResponses[fieldUcid] !== undefined && lotFieldResponses[fieldUcid] !== '') {
        bidValue = lotFieldResponses[fieldUcid];
      }
    }
    
    if (!bidValue || bidValue === '') {
      return null;
    }
    
    // Get the top bids for this lot level field
    const lotLevelFieldsTopBids = bestBids.get('lotLevelFieldsTopBids');
    if (!lotLevelFieldsTopBids) {
      return null;
    }
    
    const currentFieldTopBids = lotLevelFieldsTopBids.get(fieldUcid);
    if (!currentFieldTopBids || !Array.isArray(currentFieldTopBids)) {
      return null;
    }
    
    const rankOfBidValue = currentFieldTopBids.indexOf(bidValue);
    if (rankOfBidValue === -1) {
      return null;
    } else {
      return indexBasedRankWiseStyles[rankOfBidValue];
    }
  }

  const getLotTotalBidDisplayValue = (supplierId) => {
    const supplierResponse = responses?.[supplierId];
    if (!supplierResponse) {
      return '';
    }
    if (
      supplierResponse.lot_total_price !== undefined
      && supplierResponse.lot_total_price !== null
      && supplierResponse.lot_total_price !== ''
    ) {
      const rawValue = supplierResponse.lot_total_price;
      return typeof rawValue === 'number' ? rawValue.toFixed(2) : rawValue.toString();
    }
    const totalsValue = supplierResponse.totals_row?.[bestBidColumnId];
    if (totalsValue !== undefined && totalsValue !== null && totalsValue !== '') {
      return typeof totalsValue === 'number' ? totalsValue.toFixed(2) : totalsValue.toString();
    }
    return '';
  };

  const getRankWiseClassForLTP = (supplierId, ucid) => {
    if (ucid !== 'extended_price' || !supplierId) {
      return null;
    }

    const bidValue = getLotTotalBidDisplayValue(supplierId);

    if (!bidValue || bidValue === '') {
      return null;
    }

    const lstLtpBestBids = bestBids.get('lstLtpBestBids');
    if (!lstLtpBestBids) {
      return null;
    }

    const ltpValues = [];
    for (let supplier in responses) {
      const formattedValue = getLotTotalBidDisplayValue(supplier);
      if (formattedValue) {
        ltpValues.push(formattedValue);
      }
    }
    
    const sortedValues = [...new Set(ltpValues)].sort((a, b) => {
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      return sortBidsAscending ? numA - numB : numB - numA;
    }).slice(0, numberOfTopBidsToHighlight);
    
    const rankOfBidValue = sortedValues.indexOf(bidValue);
    if (rankOfBidValue === -1) {
      return null;
    } else {
      return indexBasedRankWiseStyles[rankOfBidValue];
    }
  }

  const getRankWiseClassForTRP = (supplierId, ucid) => {
    const isTrpBidPriceColumn = (
      ucid === 'extended_price'
      || ucid === 'tranformed_price'
      || ucid === 'total_cost'
      || ucid === bestBidColumnId
    );
    if (!isTrpBidPriceColumn || !supplierId) {
      return null;
    }

    const getTransformationPriceNumber = (sid) => {
      const supplierResponse = responses?.[sid];
      if (!supplierResponse) {
        return null;
      }
      const rawValue = (
        supplierResponse.transformation_price
        ?? supplierResponse?.totals_row?.transformation_price
        ?? supplierResponse?.totals_row?.tranformed_price
      );
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return null;
      }
      const numericValue = getNumberFromString(rawValue);
      return Number.isFinite(numericValue) ? numericValue : null;
    };

    const bidValue = getTransformationPriceNumber(supplierId);
    if (bidValue === null) {
      return null;
    }

    const transformedPriceValues = Object.keys(responses || {}).reduce((acc, sid) => {
      const value = getTransformationPriceNumber(sid);
      if (value !== null) {
        acc.push(value);
      }
      return acc;
    }, []);

    const sortedValues = [...new Set(transformedPriceValues)]
      .sort((a, b) => (sortBidsAscending ? a - b : b - a))
      .slice(0, numberOfTopBidsToHighlight);
    const rankOfBidValue = sortedValues.indexOf(bidValue);

    if (rankOfBidValue === -1) {
      return null;
    }
    return indexBasedRankWiseStyles[rankOfBidValue];
  }

  const getRankWiseClassForLST = (supplierId, ucid) => {
    if (ucid !== 'extended_price' || !supplierId) {
      return null;
    }
    if (shouldHidePostAuctionSupplierTotals(supplierId)) {
      return null;
    }

    const bidValue = getNumberFromString(responses?.[supplierId]?.lot_sub_total);
    if (!Number.isFinite(bidValue) || bidValue <= 0) {
      return null;
    }

    const lstValues = [];
    for (let supplier in responses) {
      if (shouldHidePostAuctionSupplierTotals(supplier)) {
        continue;
      }
      const lstValue = getNumberFromString(responses[supplier]?.lot_sub_total);
      if (Number.isFinite(lstValue) && lstValue > 0) {
        lstValues.push(lstValue);
      }
    }

    const sortedValues = [...new Set(lstValues)]
      .sort((a, b) => (sortBidsAscending ? a - b : b - a))
      .slice(0, numberOfTopBidsToHighlight);

    const rankOfBidValue = sortedValues.indexOf(bidValue);
    if (rankOfBidValue === -1) {
      return null;
    }
    return indexBasedRankWiseStyles[rankOfBidValue];
  }

  const getRowHeight = ({ index }) => {
    if (index === 0) {
      return getHeaderHeight();
    }
    
    // Check if this is a lot level field row (after line items)
    const rowIndex = index - 1;
    const isLotLevelRow = rowIndex >= lineItems.length;
    
    if (isLotLevelRow) {
      // Lot level fields rows always have standard height
      return 27;
    }
    
    const row = lineItems[rowIndex];
    const count = responsesCountByRow.get(row.urid)
    if (
      showLineItemsWithNoResponse 
      && ( !(count === undefined || count === 0 ) || responseNeededForAllLineItems) 
    ) {
      return 0;
    }
    return 27;
  }

  const getColumnInstance = (rowIndex, originalColumnIndex) => {
    let columnIndex = originalColumnIndex;
    // =======================================
    // bid action - b - lot level bid
    //     - here two columns are not rendered
    //         1. quantity
    //         2. unit of measurement
    //     - hence total 6 buyer columns
    // bi action - a, c will have 8 buyer
    // columns
    // ========================================
    const AWARD_SAVINGS_INDEX = bidAction === 'b' ? 6 : 8;
    if (isAwardDraft) {
      if (originalColumnIndex < AWARD_SAVINGS_INDEX) {
        // ==============================
        // This is buyer filled column
        // only subtract excluded/ignored
        // two checkbox type columns
        // ==============================
        columnIndex -= 2;
      }
      else {
        // ===============================
        // This is some supplier column
        // subtract all award related 
        // columns which are total 3 in 
        // quantity two exclude/ignore and
        // one award savings column
        // ===============================
        columnIndex -= 3;
      }
    }
    if (isAwardDraft) {
      if (originalColumnIndex < 2) {
        // =============================
        // originalIndex = 0 -> excluded
        // originalIndex = 1 -> ignored
        // =============================
        return new Column(awardColumns[originalColumnIndex]);
      }
      if (originalColumnIndex === AWARD_SAVINGS_INDEX) {
        // ===========================
        // return award savings column
        // ===========================
        return new Column(awardColumns[2]);
      }
    }
    if (columnIndex < buyerColumns.length) {
      return new Column({
        ...buyerColumns[columnIndex],
        isAwardDraft,
        optimizedAt
      });
    }
    return getSupplierColumn(columnIndex);
  }

  const onExcludeIgnore = e => {
    const { id, checked } = e.target;
    const [
      _prefix,
      rowId,
      field
    ] = id.split('-');
    /**
     * This is a function which can toggle
     * some specific field name (ignored/excluded)
     * 
     *     @example
     *     if fieldName == 'ignored' and checked == (true/false) {
     *         simply add/remove rowId from
     *         that corresponding array
     *     }
     * 
     *     @example
     *     if fieldName == 'excluded' and checked == true {
     *         simply add rowId from excluded array
     *     }
     * 
     *     @example
     *     if fieldName == 'excluded' and checked == false {
     *         remove rowId from excluded array
     *         remove rowId from ignored as well
     *     }
     * 
     * @param {*} oldShares - current shares state
     * @param {*} fieldName - name of field to toggle
     * @returns {*} - modified state of shares to set
     */
    const createNewShares = (oldShares, fieldName) => {
      let excludedIgnoredRows;
      const accessorField: string = (
        (fieldName === 'excluded')
        ? EXCLUDED_FIELD
        : IGNORED_FIELD
      );
      const line_item_level_shares = {
        ...oldShares.line_item_level_shares
      };
      if (fieldName === 'excluded') {
        delete line_item_level_shares[rowId];
      }
      if (checked) {
        excludedIgnoredRows = ((oldShares[accessorField]: any) || []).concat(rowId);
      }
      else {
        excludedIgnoredRows = ((oldShares[accessorField]: any) || []).filter(urid => urid !== rowId);
      }
      return ({
        ...oldShares,
        line_item_level_shares,
        [accessorField]: excludedIgnoredRows
      });
    }
    setShares(shares => {
      if (checked === true || field === 'ignored') {
        // =====================
        // if checked or ignored
        // go with normal flow
        // =====================
        return createNewShares(shares, field);
      }
      else {
        // ========================
        // if excluded is unchecked
        // also remove ignored
        // ========================
        return createNewShares(
          createNewShares(shares, field),
          'ignored'
        );
      }
    });
  }

  const onExcludeAllChange = e => {
    const { checked } = e.target;
    if (!checked) {
      // ==================================
      // uncheck exlude all line items
      // we need to uncheck ignored as well
      // ================================== 
      setShares(shares => ({
        ...shares,
        [EXCLUDED_FIELD]: [],
        [IGNORED_FIELD]: [],
      }));
    }
    else {
      // ======================
      // Exclude all line items
      // remove all line shares
      // ======================
      const checkbox_value = data.lineItems.map(l => l.urid);
      setShares(shares => ({
        ...shares,
        [EXCLUDED_FIELD]: checkbox_value,
        line_item_level_shares: {}
      }));
    }
  }

  const onIgnoreAllChange = e => {
    const { checked } = e.target;
    if (!checked) {
      // ==================================
      // uncheck exlude all line items
      // we need to uncheck ignored as well
      // ================================== 
      setShares(shares => ({
        ...shares,
        [IGNORED_FIELD]: [],
      }));
    }
    else {
      // ======================
      // Exclude all line items
      // remove all line shares
      // ======================
      setShares(shares => ({
        ...shares,
        [IGNORED_FIELD]: Array.from(excludedSet),
        line_item_level_shares: {}
      }));
    }
  }

  const handleBaselinePayload = (e, lineItemUrid, rowIndex, ucid) => {
    const payload = {
      newValue: e.target.value,
      lineItemUrid: lineItemUrid,
      rowIndex: rowIndex,
      ucid: ucid
    }
    changeBaselineCell(payload);
  }

  const handleFormatBaseline = (e, rowIndex, columnIndex, lineItemUrid, ucid) => {
    const payload = {
      value: e.target.value,
      decimals: buyerColumns[columnIndex].decimal_spaces,
      rowIndex: rowIndex,
      lineItemUrid: lineItemUrid,
      ucid: ucid
    }
    formatBaseline(payload);
  }

  const handleBaselinePaste = (e, rowIndex, columnIndex, ucid) => {
    const pastedData = parseHTML(e);
    if (!(Array.isArray(pastedData) && (pastedData.length > 0 || pastedData[0].length > 0))) {
      return;
    }
    e.preventDefault();
    const payload = {
      pastedData: pastedData,
      rowIndex: rowIndex,
      columnIndex: columnIndex,
      ucid: ucid
    }
    pasteBaseline(payload);
  }

  const getColumnValue = (rowIndex, columnIndex) => {
    if (rowIndex <= -1) {
      return null;
    }
    
    // Check if this is a lot level field row (after line items)
    const isLotLevelRow = rowIndex >= lineItems.length;
    
    if (isLotLevelRow) {
      // For lot level rows, return null to indicate no value processing needed
      // The rendering is handled separately in renderLotLevelRow
      return null;
    }
    
    const lineItem = lineItems[rowIndex];
    const columnInstance = getColumnInstance(rowIndex, columnIndex);
    const isIgnored = ignoredSet.has(lineItem.urid);
    if (columnInstance.isBuyerFilled) {
      const buyerColumn = columnInstance;
      if (buyerColumn.ucid === 'sr_no') {
        return rowIndex + 1
      }

      const getIfExcluded = lineItem => {
        const count = responsesCountByRow.get(lineItem.urid)
        const isEmpty = (count === undefined || count === 0) && !responseNeededForAllLineItems;
        const { urid: rowId } = lineItem;
        let isExcluded = false;
        if (rowId) {
          // ===================================================
          // convert urid to string because this comes as number
          // in case of bid action 3 while `checkbox_value` is
          // always stored in array of string in award object
          // ===================================================
          const rowIdInString = `${rowId}`;
          isExcluded = excludedSet.has(rowIdInString) || isEmpty || false;
        }
        return isExcluded;
      }

      const getIfIgnored = lineItem => {
        const count = responsesCountByRow.get(lineItem.urid)
        const isEmpty = (count === undefined || count === 0) && !responseNeededForAllLineItems;
        const { urid: rowId } = lineItem;
        let isIgnored = false;
        if (rowId) {
          // ======================================================
          // convert urid to string because this comes as number
          // in case of bid action 3 while `baseline_checkbox_value`
          // is always stored in array of string in award object
          // =======================================================
          const rowIdInString = `${rowId}`;
          isIgnored = ignoredSet.has(rowIdInString) || isEmpty || false;
        }
        return isIgnored;
      }

      if (buyerColumn.ucid === AWARD_COLUMN_IDS.excluded) {
        // ============================
        // calculate if row is excluded
        // ============================
        const count = responsesCountByRow.get(lineItem.urid)
        const isEmpty = (count === undefined || count === 0) && !responseNeededForAllLineItems;
        const isExcluded = getIfExcluded(lineItem);
        return (
          <CustomCheckBox
            checked={isExcluded}
            disabled={viewAwardScenario || isEmpty}
            onChange={onExcludeIgnore}
            id={'row-' + lineItem.urid + '-excluded'}
          />
        );
      }
      if (buyerColumn.ucid === AWARD_COLUMN_IDS.ignored) {
        // ===========================
        // calculate if row is ignored
        // ===========================
        const isExcluded = getIfExcluded(lineItem);
        const count = responsesCountByRow.get(lineItem.urid)
        const isEmpty = (count === undefined || count === 0) && !responseNeededForAllLineItems;
        const isIgnored = getIfIgnored(lineItem);
        return (
          <CustomCheckBox
            checked={isIgnored}
            disabled={viewAwardScenario || !isExcluded || isEmpty}
            onChange={onExcludeIgnore}
            id={'row-' + lineItem.urid + '-ignored'}
          />
        );
      }
      
      const innerValue = getSafe(() => lineItem[buyerColumn.ucid]);
      let renderValues = true;
      let eventSavings = '';
      let awardSavings = '';
      const { ucid } = buyerColumn;
      const count = responsesCountByRow.get(lineItem.urid)
      const isEmpty = (count === undefined || count === 0) && !responseNeededForAllLineItems;
      const isEditing = isColumnEditable && (!isEmpty || showLineItemsWithNoResponse);
      if (
        isIgnored ||
        ucid === 'baseline_price'
        && isEmpty
        && !showLineItemsWithNoResponse
      ) {
        renderValues = false;
      }
      else if (!isIgnored && ucid === 'event_saving') {
        eventSavings = getEventSavings(lineItem);
        renderValues = typeof eventSavings === 'number' || typeof eventSavings === 'string' && !isNaN(eventSavings.replace(/,/g, ''));
      }
      else if (!isIgnored && ucid === AWARD_COLUMN_IDS.award_savings) {
        const awardSavingRaw = optimizedAt === 'lot'
          ? null
          : (
            award?.line_item_award_saving?.[lineItem.urid]
            ?? award?.line_item_event_saving?.[lineItem.urid]?.event_saving
          );
        const hasAwardSavingValue = !(
          awardSavingRaw === undefined
          || awardSavingRaw === null
          || awardSavingRaw === ''
        );
        renderValues = hasAwardSavingValue;
        if (hasAwardSavingValue) {
          awardSavings = formatValue(awardSavingRaw, 2);
        }
      }
      if (isEmpty && showLineItemsWithNoResponse) {
        renderValues = true;
      }
      let bestBidDisplayValue = '';
      if (ucid === 'best_bid') {
        if (!shouldHidePostAuctionLineItemBuyerTotals && renderValues) {
          const rawBestBid = getSafe(() => lineItem[ucid]);
          if (rawBestBid && rawBestBid !== '-') {
            bestBidDisplayValue = rawBestBid;
          }
        }
      }
      const showBuyerColumnCurrency = buyerColumn.isPrice && currencySymbol && (
        ucid === 'best_bid' ? bestBidDisplayValue !== '' : renderValues
      );
      const props = {
        isEditing,
        baselineColumnStyle,
        rowIndex,
        ucid,
        handleBaselinePayload,
        lineItem,
        handleFormatBaseline,
        columnIndex,
        handleBaselinePaste,
        renderValues
      }
      const alignNameColumnLeft = buyerColumn.ucid === 'item_name' || buyerColumn.ucid === 'lot_name';
      const alignUomRight = buyerColumn.ucid === 'uom';
      const resolvedJustifyContent = alignNameColumnLeft
        ? 'flex-start'
        : alignUomRight
          ? 'flex-end'
          : buyerColumn.justifyContent;
      return (
        <span style={{
          display: 'flex',
          justifyContent: resolvedJustifyContent,
          width: alignNameColumnLeft || alignUomRight ? '100%' : undefined,
          height: '100%',
          overflow: 'hidden'
        }}>
          <span>
            {
              showBuyerColumnCurrency
            }
          </span>
          {
            buyerColumn.type === 'Text' && (
              <span
                className={tableStyles.cellContent}
                dangerouslySetInnerHTML={{
                  __html: innerValue
                }}
                title={window.htmlToFormattedText(innerValue)}
              >
            </span>
            )
          }
          {
            buyerColumn.type !== 'Text' && (
              <Fragment>
                  {
                    ucid === 'baseline_price' ? (
                      <BaselineCell
                        {...props}
                      />
                    ) : (
                      <span
                      className={tableStyles.cellContent}
                      >
                        {
                          ucid === 'event_saving' ?
                          eventSavings :
                          ucid === AWARD_COLUMN_IDS.award_savings ?
                          awardSavings:
                          ucid === 'best_bid' ?
                          bestBidDisplayValue : getSafe(() => lineItem[ucid])
                        }
                    </span>
                    )
                  }
              </Fragment>
            )
          }
          {
            buyerColumn.isPercentage
            && (
              <span className={tableStyles.suffix}>
                %
              </span>
            )
          }
        </span>
      )
    }
    else {
      const { urid } = lineItem;
      const supplierColumn = getColumnInstance(rowIndex, columnIndex);
      const supplierId = supplierColumn.safeAccess('supplier_id');
      const supplierLineItem = getSafe(() => responses[supplierId].row_data[urid]);
      const columns = supplierColumn.columns.slice();
      if (isAwardDraft) {
        columns.push(
          awardValueColumn({
            awardBy,
            isAwardDraft,
            optimizedAt
          })
        );
      }
      return columns
        .filter(({ ucid }) => !hiddenColumnSet.has(ucid))
        .map(subColumn => {
          const column = new Column(subColumn);
          if (column.isPTF) {
              // ====================================
              // 1. ptf value per supplier, per rowId
              //    is received in the
              //    `transformation_costs_data` field
              // 2. actual transformed cost is not
              //    in this key, that is sent in
              //    `supplier_responses` key
              // =====================================
            const raw_supplier_id = supplierId.toString().split('_')[0];
            return {
              value: getSafe(() => data.transformation_costs_data[raw_supplier_id][subColumn.ucid]),
              ...subColumn
            }
          }

          if (supplierLineItem) {
            return {
              value: supplierLineItem[subColumn.ucid],
              ...subColumn
            }
          }
          return subColumn
      });
    }
  }

  const onSectionRendered = () => {
    const scrollContainer = ((document.querySelector('.ra-table [role="rowgroup"]'): any): HTMLElement);
    const getWidth = (elem: HTMLElement) => elem.getBoundingClientRect().width;
    const elemWidth = getWidth(scrollContainer);
    const parentWidth = getWidth((scrollContainer.parentElement: any));
    setHideHorizontalScroll(elemWidth < parentWidth);
  }

  if (isLoading) {
    return null;
  }

  return (
    <div className={tableStyles.table}>
      <button
        onClick={onUpdate}
        id="update-lot"
      />
      <ScrollSync>
      {
        ({
          onScroll,
        }) => {
          return (
            <AutoSizer disableHeight>
              {
                ({ width, }) => {
                  return(
                    <Fragment>
                      <div className="ra-table">
                        <InnerContent
                          {...state}
                          awardBy={awardBy}
                          awardColumnStyle={awardColumnStyle}
                          onScroll={onScroll}
                          rowCount={lineItems.length + 1 + lotSummaryAdditionalRows}
                          height={totalTableHeight}
                          rowHeight={getRowHeight}
                          width={width}
                          data={responses}
                          lineItems={lineItems}
                          excludedSet={excludedSet}
                          ignoredSet={ignoredSet}
                          showLineItemsWithNoResponse={showLineItemsWithNoResponse}
                          columnCount={columnCount}
                          columnWidth={getColumnWidthHelper}
                          checkBestBid={getRankWiseClassForLineItem}
                          checkLotFieldRanking={getRankWiseClassForLotLevelField}
                          checkLTPRanking={getRankWiseClassForLTP}
                          checkTRPRanking={getRankWiseClassForTRP}
                          checkLSTRanking={getRankWiseClassForLST}
                          hiddenColumns={hiddenColumns}
                          setContentRef={setContentRef}
                          currencySymbol={currencySymbol}
                          lineErrorSet={lineErrorSet}
                          hideSuppliers={hideSuppliers}
                          _headerRenderer={_headerRenderer}
                          getColumnInstance={getColumnInstance}
                          getColumnValue={getColumnValue}
                          setShares={setShares}
                          isTransformationAppliedAtLineItemLevel={isTransformationAppliedAtLineItemLevel}
                          heights={headerHeight}
                          onSectionRendered={onSectionRendered}
                          optimizedAt={optimizedAt}
                          isAwardDraft={isAwardDraft}
                          lineItemShares={lineItemShares}
                          responsesCountByRow={responsesCountByRow}
                          responseNeededForAllLineItems={responseNeededForAllLineItems}
                          participatingSuppliersMap={participatingSuppliersMap}
                          blockedConfig={blockedConfig}
                          isColumnEditable={isColumnEditable}
                          handleBaselinePayload={handleBaselinePayload}
                          handleFormatBaseline={handleFormatBaseline}
                          handleBaselinePaste={handleBaselinePaste}
                          baselineColumnStyle={baselineColumnStyle}
                          bidAction={bidAction}
                          buyer_totals={buyer_totals}
                          lotLevelFields={lotLevelFields}
                          lotSubTotal={lotPricingSheetResponse?.data?.lot_sub_total}
                          lotTotalPrice={lotPricingSheetResponse?.data?.lot_total_price}
                          transformationPrice={lotPricingSheetResponse?.data?.transformation_price}
                          transformedBidColumns={lotPricingSheetResponse?.data?.transformedBidColumns}
                          transformationCostsData={lotPricingSheetResponse?.data?.transformation_costs_data}
                          enableLotTransformation={!!lotPricingSheetResponse?.data?.enable_lot_transformation}
                          hasLotFields={lotPricingSheetResponse?.data?.has_lot_fields}
                          totals_row={totals_row}
                          baselineTotal={baselineTotal}
                          bestBids={bestBids}
                          bestBidColumnId={bestBidColumnId}
                          eventSavingsTotal={eventSavingsTotal}
                          bestBidOfLot={bestBidOfLot}
                          awardSavingTotal={awardSavingTotal}
                          award={award}
                          getSupplierTotals={getSupplierTotals}
                          getRankWiseClassForLot={getRankWiseClassForLot}
                          buyerColumns={buyerColumns}
                          suppliersOrder={suppliersOrder}
                          suppliers={suppliers}
                          isLotExcluded={isLotExcluded}
                          getLotShare={getLotShare}
                          changeLotShare={changeLotShare}
                          isLotIgnored={isLotIgnored}
                          isLotFullyGreyedOut={isLotFullyGreyedOut}
                          hiddenColumnSet={hiddenColumnSet}
                          awardBy={awardBy}
                          eventInfo={eventInfo}
                          viewAwardScenario={viewAwardScenario}
                        />
                      </div>
                      <div
                        style={{
                          position: 'relative',
                          height: 0,
                          overflow: 'hidden'
                        }}
                      >
                        <div>
                          <InnerContent
                            {...state}
                            awardColumnStyle={awardColumnStyle}
                            onScroll={onScroll}
                            rowCount={1}
                            height={totalTableHeight}
                            rowHeight={getRowHeight}
                            width={width + sizeOfScroll}
                            data={responses}
                            lineItems={lineItems}
                            showLineItemsWithNoResponse={showLineItemsWithNoResponse}
                            columnCount={columnCount}
                            columnWidth={getColumnWidthHelper}
                            checkBestBid={getRankWiseClassForLineItem}
                            checkLotFieldRanking={getRankWiseClassForLotLevelField}
                            checkLTPRanking={getRankWiseClassForLTP}
                            checkTRPRanking={getRankWiseClassForTRP}
                            checkLSTRanking={getRankWiseClassForLST}
                            hiddenColumns={hiddenColumns}
                            currencySymbol={currencySymbol}
                            hideSuppliers={hideSuppliers}
                            _headerRenderer={_dummyHeaderRenderer}
                            getColumnInstance={getColumnInstance}
                            getColumnValue={getColumnValue}
                            lineErrorSet={lineErrorSet}
                            isDummy
                            measure={measureHeader}
                            optimizedAt={optimizedAt}
                            setContentRef={setDummyContentRef}
                            isTransformationAppliedAtLineItemLevel={isTransformationAppliedAtLineItemLevel}
                            isAwardDraft={isAwardDraft}
                            lineItemShares={lineItemShares}
                            responsesCountByRow={responsesCountByRow}
                            responseNeededForAllLineItems={responseNeededForAllLineItems}
                            participatingSuppliersMap={participatingSuppliersMap}
                            blockedConfig={blockedConfig}
                            isColumnEditable={isColumnEditable}
                            handleBaselinePayload={handleBaselinePayload}
                            handleFormatBaseline={handleFormatBaseline}
                            handleBaselinePaste={handleBaselinePaste}
                            baselineColumnStyle={baselineColumnStyle}
                            award={award}
                            eventInfo={eventInfo}
                            viewAwardScenario={viewAwardScenario}
                          />
                        </div>
                      </div>
                    </Fragment>
                )}
              }
            </AutoSizer>
          )
        }
      }
      </ScrollSync>
    </div>
  );
}

export default App;
